import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import type { ImpersonationRole } from '../../utils/impersonationSession';
import { Column } from '../../components/DataTable';
import { ConfirmModal } from '../../components/ui';
import '../../App.css';
import '../../components/ui/Button.css';
import { translateError } from '../../utils/errorMessages';

interface IMembership {
  organization: { _id: string; name: string; slug?: string };
  role: { name: string };
  isActive: boolean;
}

interface IUser {
  _id: string;
  name: string;
  email: string;
  memberships?: IMembership[];
  lastLogin?: string;
  isSuperAdmin?: boolean;
}

interface IPagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

const UsersPage: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<IUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [orgFilter, setOrgFilter] = useState('');
  const [organizations, setOrganizations] = useState<{ _id: string; name: string }[]>([]);
  const [pagination, setPagination] = useState<IPagination>({ page: 1, limit: 50, total: 0, pages: 0 });
  const [deleteTarget, setDeleteTarget] = useState<IUser | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [impersonateTarget, setImpersonateTarget] = useState<IUser | null>(null);
  const [impersonateLoading, setImpersonateLoading] = useState(false);

  const fetchUsers = useCallback(async (page: number, search: string, organizationId: string) => {
    try {
      setLoading(true);
      const response = await api.superAdmin.users.list({
        page,
        limit: 50,
        search: search || undefined,
        organizationId: organizationId || undefined,
      });
      setUsers(response.data.users || []);
      setPagination(response.data.pagination);
      setError(null);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await api.superAdmin.organizations.list();
      setOrganizations(response.data.organizations || []);
    } catch {
      // Non-critical — org filter just won't work
    }
  }, []);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  useEffect(() => {
    fetchUsers(pagination.page, searchTerm, orgFilter);
  }, [searchTerm, orgFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchUsers(1, searchTerm, orgFilter);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    fetchUsers(newPage, searchTerm, orgFilter);
  };

  const handleOrgFilterChange = (value: string) => {
    setOrgFilter(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const deleteHandler = (user: IUser) => {
    if (user.isSuperAdmin) {
      alert(t('superAdmin.users.cannotDeleteSuperAdmin'));
      return;
    }
    setDeleteTarget(user);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await api.superAdmin.users.delete(deleteTarget._id);
      fetchUsers(pagination.page, searchTerm, orgFilter);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setDeleteLoading(false);
      setDeleteTarget(null);
    }
  };

  const impersonateHandler = (user: IUser) => {
    if (user.isSuperAdmin) {
      alert(t('superAdmin.users.cannotImpersonateSuperAdmin'));
      return;
    }
    setImpersonateTarget(user);
  };

  const handleImpersonateConfirm = async () => {
    if (!impersonateTarget) return;
    setImpersonateLoading(true);
    try {
      const response = await api.superAdmin.users.impersonate(impersonateTarget._id);
      const data = response.data as unknown as {
        token: string;
        organization: { _id: string; name: string; slug: string };
        user: { _id: string; tenantUserId?: number; name: string; email: string };
        role?: ImpersonationRole;
      };

      if (!data.organization?.slug) {
        setError('Organization information missing from impersonation response');
        setImpersonateLoading(false);
        setImpersonateTarget(null);
        return;
      }

      // Build subdomain URL with token for cross-domain auth
      const protocol = window.location.protocol;
      const port = window.location.port ? `:${window.location.port}` : '';
      let hostname = window.location.hostname;

      // Remove www if present
      if (hostname.startsWith('www.')) {
        hostname = hostname.substring(4);
      }

      // Build subdomain URL by prepending org slug to the base domain
      let subdomainUrl: string;
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        // Localhost development
        subdomainUrl = `${protocol}//${data.organization.slug}.localhost${port}`;
      } else {
        // Production: prepend slug to hostname (e.g., eventik.cz -> slug.eventik.cz)
        subdomainUrl = `${protocol}//${data.organization.slug}.${hostname}${port}`;
      }

      // The target's REAL role, as returned by the impersonate endpoint. This
      // used to be a fabricated Owner role (`{ '*': 31 }`) with
      // `isSuperAdmin: true`, which made the client believe it was an admin
      // whoever was being impersonated: the UI drew the full admin surface
      // while the backend authorized each request as the impersonated member,
      // so every admin route answered 403 and permission-gated fields came back
      // empty — which read as missing data rather than as a permission problem.
      const membership = {
        organization: data.organization,
        role: data.role,
        joinedAt: new Date().toISOString(),
        isActive: true,
      };

      // Encode user data for URL (base64, Unicode-safe)
      const jsonStr = JSON.stringify({
        _id: data.user._id,
        name: data.user.name,
        email: data.user.email,
        memberships: [membership],
        currentMembership: membership,
        isSuperAdmin: false,
      });
      const userData = btoa(encodeURIComponent(jsonStr).replace(/%([0-9A-F]{2})/g, (_, p1) => String.fromCharCode(parseInt(p1, 16))));

      // Redirect to subdomain with token and user data
      window.location.href = `${subdomainUrl}/auth/impersonate?token=${encodeURIComponent(data.token)}&data=${encodeURIComponent(userData)}`;
    } catch (err: any) {
      setError(translateError(err, t));
      setImpersonateLoading(false);
      setImpersonateTarget(null);
    }
  };

  const columns: Column<IUser>[] = useMemo(() => [
    {
      header: t('common.name'),
      render: (user) => (
        <>
          {user.name}
          {user.isSuperAdmin && (
            <span className="badge badge-super-admin" style={{ marginLeft: '0.5rem' }}>
              {t('superAdmin.users.superAdmin')}
            </span>
          )}
        </>
      ),
    },
    {
      header: t('common.email'),
      key: 'email',
    },
    {
      header: t('superAdmin.users.organization'),
      render: (user) => {
        const active = user.memberships?.filter(m => m.isActive) || [];
        if (active.length === 0) return '-';
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
            {active.map((m, i) => (
              <Link
                key={i}
                to={`/super-admin/organizations/${m.organization._id}`}
                className="badge"
                style={{ textDecoration: 'none', textTransform: 'none' }}
              >
                {m.organization.name} <span style={{ opacity: 0.7 }}>({m.role?.name})</span>
              </Link>
            ))}
          </div>
        );
      },
    },
    {
      header: t('superAdmin.users.lastLogin'),
      render: (user) => user.lastLogin ? new Date(user.lastLogin).toLocaleString() : '-',
    },
  ], [t]);

  if (error) return <p className="error-message">{t('common.error')}: {error}</p>;

  return (
    <div className="admin-list-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2>{t('superAdmin.users.title')} {!loading && <span style={{ opacity: 0.6, fontSize: '0.8em' }}>({pagination.total})</span>}</h2>
      </div>

      <div className="filters-row" style={{ marginBottom: '1rem', display: 'flex', gap: '1rem' }}>
        <input
          type="text"
          placeholder={t('superAdmin.users.searchPlaceholder')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="filter-input"
          style={{ flex: 1, maxWidth: '300px' }}
        />
        <select
          value={orgFilter}
          onChange={(e) => handleOrgFilterChange(e.target.value)}
          className="filter-select"
        >
          <option value="">{t('superAdmin.users.allOrganizations')}</option>
          {organizations.map((org) => (
            <option key={org._id} value={org._id}>{org.name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <p>{t('common.loading')}</p>
      ) : users.length === 0 ? (
        <p>{t('superAdmin.users.noUsers')}</p>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  {columns.map((col, index) => (
                    <th key={index}>{col.header}</th>
                  ))}
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user._id}>
                    {columns.map((col, index) => (
                      <td key={index}>
                        {col.render ? col.render(user) : col.key ? (user as any)[col.key] : null}
                      </td>
                    ))}
                    <td>
                      <div className="grid-actions">
                        <Link
                          to={`/super-admin/users/${user._id}`}
                          className="btn btn--primary btn--sm"
                          aria-label={`${t('common.edit')} ${user.name}`}
                        >
                          <i className="fas fa-edit" aria-hidden="true"></i>
                        </Link>
                        {!user.isSuperAdmin && (
                          <>
                            <button
                              onClick={() => impersonateHandler(user)}
                              className="button-impersonate"
                              type="button"
                              title={t('superAdmin.users.impersonate')}
                              aria-label={`${t('superAdmin.users.impersonate')} ${user.name}`}
                            >
                              <i className="fas fa-user-secret" aria-hidden="true"></i>
                            </button>
                            <button
                              onClick={() => deleteHandler(user)}
                              className="btn btn--danger btn--sm"
                              type="button"
                              aria-label={`${t('common.delete')} ${user.name}`}
                            >
                              <i className="fas fa-trash" aria-hidden="true"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
              <button
                className="btn btn--secondary btn--sm"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
              >
                &laquo; {t('common.previous')}
              </button>
              <span style={{ opacity: 0.7 }}>
                {pagination.page} / {pagination.pages}
              </span>
              <button
                className="btn btn--secondary btn--sm"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
              >
                {t('common.next')} &raquo;
              </button>
            </div>
          )}
        </>
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title={t('common.confirmTitle')}
        message={t('superAdmin.users.confirmDelete')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={deleteLoading}
      />

      <ConfirmModal
        isOpen={!!impersonateTarget}
        onClose={() => setImpersonateTarget(null)}
        onConfirm={handleImpersonateConfirm}
        title={t('superAdmin.users.impersonate')}
        message={impersonateTarget ? t('superAdmin.users.confirmImpersonate', { name: impersonateTarget.name }) : ''}
        confirmText={t('superAdmin.users.impersonate')}
        cancelText={t('common.cancel')}
        variant="default"
        loading={impersonateLoading}
      />
    </div>
  );
};

export default UsersPage;
